/**
 * Per-channel statistics of an image.
 *
 * Decorrelation stretch needs the mean and the 3x3 covariance of the pixel
 * values. This is the second O(N) pass over the image, and the plan's
 * hypothesis T4 is that DStretch *subsamples* to avoid it. `sampleStats`
 * implements that subsampling; `imageStats` is the exact full pass.
 *
 * @module core/stats
 */

import type { Mat3, Raster, Stats, Vec3 } from './types.ts';
import { idx } from './mat3.ts';

/**
 * Accumulate mean and covariance over a stride of pixels.
 *
 * @param raster   RGBA buffer, `ImageData` layout.
 * @param step     Pixel stride. 1 means every pixel.
 * @param alphaCut Pixels with alpha below this are skipped. Default 0 (skip
 *                 nothing, including fully transparent pixels).
 */
export function sampleStats(raster: Raster, step = 1, alphaCut = 0): Stats {
  const { data, width, height } = raster;
  const total = width * height;
  const stride = Math.max(1, Math.floor(step));

  let n = 0;
  let sr = 0;
  let sg = 0;
  let sb = 0;
  let srr = 0;
  let sgg = 0;
  let sbb = 0;
  let srg = 0;
  let srb = 0;
  let sgb = 0;

  for (let p = 0; p < total; p += stride) {
    const px = p * 4;
    if (alphaCut > 0 && (data[px + 3] as number) < alphaCut) continue;
    const r = data[px] as number;
    const g = data[px + 1] as number;
    const b = data[px + 2] as number;
    n++;
    sr += r;
    sg += g;
    sb += b;
    srr += r * r;
    sgg += g * g;
    sbb += b * b;
    srg += r * g;
    srb += r * b;
    sgb += g * b;
  }

  if (n === 0) {
    const mean = new Float64Array(3);
    return { mean, covariance: new Float64Array(9), count: 0 };
  }

  const mean = Float64Array.of(sr / n, sg / n, sb / n);
  // Unbiased (sample) covariance would divide by n-1; for images n is huge and
  // the distinction is irrelevant, so the population form is used.
  const cov = new Float64Array(9);
  // Column-major: cov[col*3+row]. Covariance is symmetric, so the ordering of
  // the two indices inside each entry does not matter.
  cov[0] = srr / n - mean[0]! * mean[0]!; // (0,0)
  cov[1] = srg / n - mean[0]! * mean[1]!; // (1,0)
  cov[2] = srb / n - mean[0]! * mean[2]!; // (2,0)
  cov[3] = cov[1]!; // (0,1)
  cov[4] = sgg / n - mean[1]! * mean[1]!; // (1,1)
  cov[5] = sgb / n - mean[1]! * mean[2]!; // (2,1)
  cov[6] = cov[2]!; // (0,2)
  cov[7] = cov[5]!; // (1,2)
  cov[8] = sbb / n - mean[2]! * mean[2]!; // (2,2)

  return { mean, covariance: cov, count: n };
}

/** Exact statistics over every pixel. */
export function imageStats(raster: Raster, alphaCut = 0): Stats {
  return sampleStats(raster, 1, alphaCut);
}

/**
 * Per-channel mean only, over a stride: three adds per sampled pixel instead of
 * three adds and six products.
 *
 * Used by the presets whose matrix is fixed but whose output level follows the
 * image (`PresetNeeds = 'mean'`). On live video that is the difference between
 * a cheap frame and an expensive one.
 */
export function sampleMean(raster: Raster, step = 1, alphaCut = 0): Vec3 {
  const { data, width, height } = raster;
  const total = width * height;
  const stride = Math.max(1, Math.floor(step));

  let n = 0;
  let sr = 0;
  let sg = 0;
  let sb = 0;
  for (let p = 0; p < total; p += stride) {
    const px = p * 4;
    if (alphaCut > 0 && (data[px + 3] as number) < alphaCut) continue;
    sr += data[px] as number;
    sg += data[px + 1] as number;
    sb += data[px + 2] as number;
    n++;
  }
  if (n === 0) return new Float64Array(3);
  return Float64Array.of(sr / n, sg / n, sb / n);
}

/**
 * Wrap a mean in a `Stats` with a zero covariance.
 *
 * Safe only for presets that never read the covariance — the `mean`-needs ones,
 * whose `build` returns a fixed matrix. Passing this to a `covariance`-needs
 * preset would ask `jacobiEigen` for the eigenvectors of the zero matrix.
 */
export function meanOnlyStats(mean: Vec3): Stats {
  return { mean, covariance: new Float64Array(9), count: 0 };
}

/**
 * Choose a stride that visits at most `maxPixels` pixels. Used for live video,
 * where a full pass at 30 fps is wasted work (hypothesis T4).
 */
export function strideFor(raster: Raster, maxPixels = 20000): number {
  const total = raster.width * raster.height;
  if (total <= maxPixels) return 1;
  return Math.ceil(total / maxPixels);
}

/** Statistics over a list of `[r, g, b]` triples. Used by tests and `solve.ts`. */
export function statsFromSamples(samples: ArrayLike<ArrayLike<number>>): Stats {
  const n = samples.length;
  if (n === 0) {
    return { mean: new Float64Array(3), covariance: new Float64Array(9), count: 0 };
  }
  const mean = new Float64Array(3);
  for (let i = 0; i < n; i++) {
    mean[0] = mean[0]! + samples[i]![0]!;
    mean[1] = mean[1]! + samples[i]![1]!;
    mean[2] = mean[2]! + samples[i]![2]!;
  }
  mean[0] = mean[0]! / n;
  mean[1] = mean[1]! / n;
  mean[2] = mean[2]! / n;

  const cov = new Float64Array(9);
  for (let i = 0; i < n; i++) {
    const dr = samples[i]![0]! - mean[0]!;
    const dg = samples[i]![1]! - mean[1]!;
    const db = samples[i]![2]! - mean[2]!;
    cov[0] = cov[0]! + dr * dr;
    cov[1] = cov[1]! + dg * dr;
    cov[2] = cov[2]! + db * dr;
    cov[4] = cov[4]! + dg * dg;
    cov[5] = cov[5]! + db * dg;
    cov[8] = cov[8]! + db * db;
  }
  cov[0] = cov[0]! / n;
  cov[1] = cov[1]! / n;
  cov[2] = cov[2]! / n;
  cov[3] = cov[1]!;
  cov[4] = cov[4]! / n;
  cov[5] = cov[5]! / n;
  cov[6] = cov[2]!;
  cov[7] = cov[5]!;
  cov[8] = cov[8]! / n;
  return { mean, covariance: cov, count: n };
}

/**
 * Sample a multivariate normal `N(mean, covariance)` by Cholesky.
 *
 * Used by the statistics test to build data with a *known* covariance, then
 * check that `imageStats` recovers it.
 */
export function cholesky(cov: Mat3): Mat3 {
  const l = new Float64Array(9);
  const c = (r: number, col: number): number => cov[idx(r, col)]!;
  const setL = (r: number, col: number, v: number): void => {
    l[idx(r, col)] = v;
  };

  const l00 = Math.sqrt(Math.max(c(0, 0), 0));
  setL(0, 0, l00);
  const l10 = l00 > 0 ? c(1, 0) / l00 : 0;
  setL(1, 0, l10);
  const l20 = l00 > 0 ? c(2, 0) / l00 : 0;
  setL(2, 0, l20);
  const l11 = Math.sqrt(Math.max(c(1, 1) - l10 * l10, 0));
  setL(1, 1, l11);
  const l21 = l11 > 0 ? (c(2, 1) - l20 * l10) / l11 : 0;
  setL(2, 1, l21);
  const l22 = Math.sqrt(Math.max(c(2, 2) - l20 * l20 - l21 * l21, 0));
  setL(2, 2, l22);
  return l;
}

/** Standard deviation of each channel: `sqrt(diag(cov))`. */
export function sigmas(cov: Mat3): Vec3 {
  return Float64Array.of(
    Math.sqrt(Math.max(cov[0]!, 0)),
    Math.sqrt(Math.max(cov[4]!, 0)),
    Math.sqrt(Math.max(cov[8]!, 0)),
  );
}

/** Correlation matrix derived from a covariance. */
export function correlation(cov: Mat3): Mat3 {
  const s = sigmas(cov);
  const out = new Float64Array(9);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const denom = s[r]! * s[c]!;
      out[idx(r, c)] = denom > 0 ? cov[idx(r, c)]! / denom : 0;
    }
  }
  return out;
}
